/**
 * Object Type Utilities
 *
 * Shared functions for normalizing object categories, type checking,
 * and visibility filtering. Extends objectCategoryHelper.js.
 */

// Re-export everything from objectCategoryHelper
export {
  isFixture,
  isFurnishing,
  COUNTERTOP_PARENT_TYPES,
  canHaveCountertopOnTop,
  objectCanHaveCountertop,
} from './objectCategoryHelper';
export { filterObjectsByVisibility } from './objectVisibility';

/**
 * Normalize object category to a string type
 * Handles various category formats:
 * - String: "countertop" -> "countertop"
 * - Object: { countertop: true } -> "countertop"
 * - Object with multiple: { "lower-cabinet": true } -> "lower-cabinet"
 *
 * @param {string|Object} category - Category in any format
 * @returns {string|null} Normalized category string or null
 */
export const normalizeCategory = (category) => {
  if (!category) return null;

  if (typeof category === 'string') {
    return category.toLowerCase();
  }

  if (typeof category === 'object' && category !== null) {
    // Return the first key that has a truthy value
    const keys = Object.keys(category);
    for (const key of keys) {
      if (category[key]) {
        return key.toLowerCase();
      }
    }
  }

  return null;
};

/**
 * Get object type from type field or category
 * Prefers type field, falls back to category
 *
 * @param {Object} obj - Object with type and/or category fields
 * @returns {string|null} Object type string or null
 */
export const getObjectType = (obj) => {
  if (!obj) return null;

  // Prefer type field (handle both string and object formats)
  if (obj.type) {
    if (typeof obj.type === 'string') {
      return obj.type.toLowerCase();
    }
    // Object format: { "ceiling-light": true } -> "ceiling-light"
    const normalized = normalizeCategory(obj.type);
    if (normalized) return normalized;
  }

  // Fall back to category
  return normalizeCategory(obj.category);
};

/**
 * Check if object matches a specific type
 * Handles both type field and category object
 *
 * @param {Object} obj - Object to check
 * @param {string} targetType - Type to match (e.g., 'countertop', 'lower-cabinet')
 * @returns {boolean} True if object matches the type
 */
export const isObjectType = (obj, targetType) => {
  if (!obj || !targetType) return false;

  const target = targetType.toLowerCase();

  // Check type field
  const type = obj.type?.toLowerCase() || '';
  if (type === target) return true;

  // Check category
  if (obj.category) {
    if (typeof obj.category === 'string') {
      return obj.category.toLowerCase() === target;
    }
    if (typeof obj.category === 'object' && obj.category !== null) {
      return obj.category[target] === true || obj.category[targetType] === true;
    }
  }

  return false;
};

// ============================================
// Type-specific predicates
// ============================================

/**
 * Check if object is a countertop
 */
export const isCountertop = (obj) => isObjectType(obj, 'countertop');

/**
 * Check if object is a cabinet (lower or upper)
 */
export const isCabinet = (obj) =>
  isObjectType(obj, 'lower-cabinet') || isObjectType(obj, 'upper-cabinet');

/**
 * Check if object is a lower cabinet
 */
export const isLowerCabinet = (obj) => isObjectType(obj, 'lower-cabinet');

/**
 * Check if object is an upper cabinet
 */
export const isUpperCabinet = (obj) => isObjectType(obj, 'upper-cabinet');

/**
 * Check if object is a fireplace
 */
export const isFireplace = (obj) => {
  if (!obj) return false;
  const type = obj.type?.toLowerCase() || '';
  if (type === 'fireplace') return true;
  // Handle category.fireplace being truthy (either true or {})
  if (obj.category?.fireplace) return true;
  return false;
};

/**
 * Check if object is a tub surface
 */
export const isTubSurface = (obj) => isObjectType(obj, 'tub-surface');

/**
 * Check if object is a tub support box
 */
export const isTubSupportBox = (obj) => isObjectType(obj, 'tub-support-box');

/**
 * Check if object is a mirror
 */
export const isMirror = (obj) => isObjectType(obj, 'mirror');

/**
 * Check if object has a GLB model attached
 */
export const hasGlbModel = (obj) => {
  if (!obj) return false;
  return !!(obj.modelUrl || obj.modelId);
};

// ============================================
// Rendering decisions
// ============================================

/**
 * Check if object should be rendered as a SceneObject (GLB loader)
 * Returns false for objects that have specialized procedural renderers
 *
 * @param {Object} obj - Object to check
 * @returns {boolean} True if object should use SceneObject component
 */
export const shouldRenderAsSceneObject = (obj) => {
  if (!obj) return false;

  // Skip countertops - rendered separately with CSG support
  if (isCountertop(obj)) return false;

  // Skip tub surfaces - rendered separately with CSG support
  if (isTubSurface(obj)) return false;

  // Skip tub support boxes - rendered separately with CSG support
  if (isTubSupportBox(obj)) return false;

  // Skip fireplaces - rendered separately with procedural geometry
  if (isFireplace(obj)) return false;

  // Skip cabinets - rendered separately with procedural geometry
  if (isCabinet(obj)) return false;

  // Skip mirrors WITHOUT a GLB model - rendered with procedural Reflector
  // Mirrors WITH a modelUrl should be rendered as GLB models
  if (isMirror(obj) && !hasGlbModel(obj)) return false;

  return true;
};
